/* eslint-disable react/prop-types */
import { useEffect, useRef } from 'react';
import { AvailableDate } from '../../../services/calendar/types/calenderTypes';

interface StatusPanelProps {
  handleDayClick: (date: Date) => void;
  dateRangeStatus: AvailableDate[];
  activeMonth: Date;
  recentlyClickedDate?: Date | null;
  availableFilter: 'all' | 'weekdays' | 'weekends';
  selectedDaysOfWeek: number[];
}

export const CalendarStatus: React.FC<StatusPanelProps> = ({
  handleDayClick,
  dateRangeStatus,
  activeMonth,
  recentlyClickedDate,
  availableFilter,
  selectedDaysOfWeek,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const clickedDateRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to recently clicked date
  useEffect(() => {
    if (recentlyClickedDate && clickedDateRef.current && scrollContainerRef.current) {
      clickedDateRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [recentlyClickedDate]);

  // Show filtered dates OR opt-in available dates for current month
  const allDatesToShow = dateRangeStatus.filter(dateInfo => {
    const isCorrectMonth =
      dateInfo.date.getMonth() === activeMonth.getMonth() &&
      dateInfo.date.getFullYear() === activeMonth.getFullYear();

    if (!isCorrectMonth) return false;

    // Check if date is in current filter (matching useCalendar logic)
    const day = dateInfo.date.getDay();
    const isInCurrentFilter = (() => {
      if (availableFilter === 'weekdays') {
        return day >= 1 && day <= 5;
      }
      if (availableFilter === 'weekends') {
        return day === 0 || day === 6;
      }
      return selectedDaysOfWeek.includes(day);
    })();

    // Show if: filtered date OR (non-filtered AND available)
    return isInCurrentFilter || (dateInfo.isManuallyAdded && dateInfo.isAvailable);
  });

  return (
    <div className="flex h-full min-h-0 flex-col rounded-lg border border-gray-200 bg-gray-50 p-6 dark:border-neutral-700 dark:bg-neutral-900 md:w-2/5">
      <div ref={scrollContainerRef} className="flex-grow space-y-2 overflow-y-auto pb-2 pr-2">
        {dateRangeStatus.length > 0 ? (
          allDatesToShow.length > 0 ? (
            allDatesToShow.map(dateInfo => {
              const isRecentlyClicked =
                recentlyClickedDate &&
                dateInfo.date.toDateString() === recentlyClickedDate.toDateString();

              return (
                <div
                  key={dateInfo.date.toISOString()}
                  ref={isRecentlyClicked ? clickedDateRef : null}
                  onClick={() => handleDayClick(dateInfo.date)}
                  className={`mx-1 my-1 flex cursor-pointer items-center justify-between rounded-lg p-3 transition-all duration-200 hover:shadow-md ${
                    dateInfo.isAvailable
                      ? 'bg-gradient-to-r from-pink-500/10 to-purple-600/10 hover:from-pink-500/20 hover:to-purple-600/20'
                      : 'bg-neutral-500/10 hover:bg-neutral-500/20'
                  } ${
                    isRecentlyClicked ? 'ring-2 ring-pink-500 ring-opacity-50 ring-offset-2' : ''
                  }`}
                >
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                    {dateInfo.date.toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                    })}
                  </span>
                  <span
                    className={`rounded-full px-2 py-1 text-xs font-bold text-white ${
                      dateInfo.isAvailable
                        ? 'bg-gradient-to-br from-pink-500 to-purple-600'
                        : 'bg-neutral-600'
                    }`}
                  >
                    {dateInfo.isAvailable ? 'AVAILABLE' : 'BUSY'}
                  </span>
                </div>
              );
            })
          ) : (
            <div className="flex h-full items-center justify-center">
              <p className="text-center text-gray-500 dark:text-gray-400">
                No dates to show for this month.
              </p>
            </div>
          )
        ) : (
          <div className="flex h-full items-center justify-center">
            <p className="text-center text-gray-500 dark:text-gray-400">
              Import a calendar to view date statuses.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
